import React, { useState } from 'react';
import { QAValidationResult, QAValidationDetail } from '../services/api';
import './QAValidationResults.css';

interface QAValidationResultsProps {
  validation: QAValidationResult;
}

const QAValidationResults: React.FC<QAValidationResultsProps> = ({ validation }) => {
  const [expandedDetails, setExpandedDetails] = useState<Set<number>>(new Set());

  const toggleDetail = (index: number) => {
    const newExpanded = new Set(expandedDetails);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedDetails(newExpanded);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'passed':
        return '✓';
      case 'failed':
        return '✗';
      case 'error':
        return '⚠';
      case 'warning':
        return '⚡';
      default:
        return '•';
    }
  };

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'passed':
        return 'status-passed';
      case 'failed':
        return 'status-failed';
      case 'error':
        return 'status-error';
      case 'warning':
        return 'status-warning';
      default:
        return 'status-unknown';
    }
  };

  const getOverallStatusMessage = () => {
    switch (validation.overall_status) {
      case 'passed':
        return 'All validations passed successfully';
      case 'failed':
        return 'Some validations failed';
      case 'error':
        return 'Validation encountered errors';
      case 'warning':
        return 'Validations passed with warnings';
      default:
        return 'Validation complete';
    }
  };

  return (
    <div className="qa-validation-results">
      <h4>QA Validation Results</h4>
      
      <div className={`overall-status ${getStatusClass(validation.overall_status)}`}>
        <div className="status-header">
          <span className="status-icon">{getStatusIcon(validation.overall_status)}</span>
          <span className="status-message">{getOverallStatusMessage()}</span>
        </div>
        
        <div className="summary-stats">
          <div className="stat-item">
            <span className="stat-value">{validation.summary.total}</span>
            <span className="stat-label">Total</span>
          </div>
          <div className="stat-item passed">
            <span className="stat-value">{validation.summary.passed}</span>
            <span className="stat-label">Passed</span>
          </div>
          {validation.summary.failed > 0 && (
            <div className="stat-item failed">
              <span className="stat-value">{validation.summary.failed}</span>
              <span className="stat-label">Failed</span>
            </div>
          )}
          {validation.summary.errors > 0 && (
            <div className="stat-item error">
              <span className="stat-value">{validation.summary.errors}</span>
              <span className="stat-label">Errors</span>
            </div>
          )}
        </div>
      </div>

      <div className="validation-details">
        <h5>Validation Details</h5>
        {validation.details.map((detail: QAValidationDetail, index: number) => (
          <div 
            key={index} 
            className={`validation-item ${getStatusClass(detail.status)}`}
          >
            <div 
              className="validation-header"
              onClick={() => toggleDetail(index)}
            >
              <span className="validation-icon">{getStatusIcon(detail.status)}</span>
              <span className="validation-name">{detail.validation}</span>
              {detail.message && (
                <span className="validation-message">{detail.message}</span>
              )}
              <span className="expand-icon">
                {expandedDetails.has(index) ? '▼' : '▶'}
              </span>
            </div>
            
            {expandedDetails.has(index) && (
              <div className="validation-content">
                {Object.keys(detail.checks).length > 0 && (
                  <div className="checks-section">
                    <h6>Checks Performed:</h6>
                    {Object.entries(detail.checks).map(([checkName, checkResult]: [string, any]) => (
                      <div key={checkName} className="check-item">
                        <span className="check-name">{checkName}:</span>
                        <span className={`check-status ${getStatusClass(checkResult.status)}`}>
                          {checkResult.status} - {checkResult.message}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                
                {detail.issues.length > 0 && (
                  <div className="issues-section">
                    <h6>Issues Found:</h6>
                    <ul className="issues-list">
                      {detail.issues.map((issue, idx) => (
                        <li key={idx} className="issue-item">{issue}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
      
      <div className="validation-timestamp">
        Validated at: {new Date(validation.executed_at * 1000).toLocaleString()}
      </div>
    </div>
  );
};

export default QAValidationResults;